import React from 'react';
import { cn } from '../utils/cn';

export type ChipVariant = 'assist' | 'filter' | 'input' | 'suggestion';
export type ChipTone =
  | 'neutral'
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'success'
  | 'warning'
  | 'destructive'
  | 'info';

export interface ChipProps extends React.HTMLAttributes<HTMLSpanElement> {
  variant?: ChipVariant;
  tone?: ChipTone;
  selected?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  onRemove?: () => void;
  size?: 'sm' | 'md';
  asButton?: boolean;
}

const toneFilledMap: Record<ChipTone, string> = {
  neutral: 'bg-surface-variant text-surface-variant-foreground border border-outline/40',
  primary: 'bg-primary-container text-primary-container-foreground border border-primary/30',
  secondary: 'bg-secondary-container text-secondary-container-foreground border border-secondary/30',
  tertiary: 'bg-tertiary-container text-tertiary-container-foreground border border-tertiary/30',
  success: 'bg-success-container text-success-container-foreground border border-success/30',
  warning: 'bg-warning-container text-warning-container-foreground border border-warning/30',
  destructive: 'bg-error-container text-error-container-foreground border border-destructive/30',
  info: 'bg-info-container text-info-container-foreground border border-info/30',
};

const toneOutlineMap: Record<ChipTone, string> = {
  neutral: 'border border-outline text-foreground hover:bg-surface-variant',
  primary: 'border border-primary/40 text-primary hover:bg-primary/10',
  secondary: 'border border-secondary/50 text-secondary-foreground hover:bg-secondary/15',
  tertiary: 'border border-tertiary/50 text-tertiary-foreground hover:bg-tertiary/15',
  success: 'border border-success/40 text-success hover:bg-success/10',
  warning: 'border border-warning/40 text-warning-foreground hover:bg-warning/15',
  destructive: 'border border-destructive/40 text-destructive hover:bg-error-container',
  info: 'border border-info/40 text-info hover:bg-info-container',
};

const sizeMap = {
  sm: 'h-6 px-2 text-label-sm gap-1',
  md: 'h-8 px-3 text-body-md gap-1.5',
};

export const Chip: React.FC<ChipProps> = ({
  children,
  variant = 'assist',
  tone = 'neutral',
  selected = false,
  size = 'md',
  leftIcon,
  rightIcon,
  onRemove,
  asButton,
  className,
  onClick,
  ...props
}) => {
  const interactive = Boolean(onClick) || asButton || variant === 'filter';
  const useFilled = selected || variant === 'input' || (variant === 'filter' && selected);
  const styles = useFilled ? toneFilledMap[tone] : toneOutlineMap[tone];

  const Tag: any = interactive ? 'button' : 'span';

  return (
    <Tag
      className={cn(
        'inline-flex items-center justify-center rounded-sm whitespace-nowrap transition-colors duration-2 ease-standard',
        'focus-visible:outline-none focus-visible:shadow-focus-ring',
        sizeMap[size],
        styles,
        interactive && 'cursor-pointer',
        className,
      )}
      onClick={onClick}
      {...(interactive ? { type: 'button' as const } : {})}
      aria-pressed={variant === 'filter' ? selected : undefined}
      {...props}
    >
      {leftIcon ? (
        <span className="inline-flex shrink-0" aria-hidden>
          {leftIcon}
        </span>
      ) : null}
      <span className="truncate">{children}</span>
      {rightIcon && !onRemove ? (
        <span className="inline-flex shrink-0" aria-hidden>
          {rightIcon}
        </span>
      ) : null}
      {onRemove ? (
        <button
          type="button"
          aria-label="حذف"
          onClick={(event) => {
            event.stopPropagation();
            onRemove();
          }}
          className="inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-current opacity-70 transition-opacity hover:opacity-100 focus-visible:outline-none focus-visible:shadow-focus-ring"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} className="h-3 w-3">
            <path d="M18 6 6 18M6 6l12 12" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      ) : null}
    </Tag>
  );
};
